import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Edit, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface TemplateRow {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  is_active: boolean;
  preview_image_url: string | null;
  template_data: any;
}

export function LandingPageTemplatesModule() {
  const [templates, setTemplates] = useState<TemplateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<TemplateRow | null>(null);

  const [form, setForm] = useState({
    name: "",
    description: "",
    category: "general",
    is_active: true,
    preview_image_url: "",
    template_data: "{\n  \"sections\": []\n}",
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("landing_page_templates")
        .select("id, name, description, category, is_active, preview_image_url, template_data")
        .order("created_at", { ascending: false });
      if (error) throw error;
      setTemplates(data || []);
    } catch (e) {
      console.error(e);
      toast.error("Fehler beim Laden der Templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", description: "", category: "general", is_active: true, preview_image_url: "", template_data: "{\n  \"sections\": []\n}" });
    setModalOpen(true);
  };

  const openEdit = (tpl: TemplateRow) => {
    setEditing(tpl);
    setForm({
      name: tpl.name,
      description: tpl.description || "",
      category: tpl.category || "general",
      is_active: tpl.is_active,
      preview_image_url: tpl.preview_image_url || "",
      template_data: JSON.stringify(tpl.template_data ?? {}, null, 2),
    });
    setModalOpen(true);
  };

  const save = async () => {
    let parsed: any = {};
    try {
      parsed = form.template_data ? JSON.parse(form.template_data) : {};
    } catch {
      toast.error("Template-Daten sind kein gültiges JSON");
      return;
    }
    try {
      if (editing) {
        const { error } = await supabase
          .from("landing_page_templates")
          .update({
            name: form.name,
            description: form.description || null,
            category: form.category || null,
            is_active: form.is_active,
            preview_image_url: form.preview_image_url || null,
            template_data: parsed,
          })
          .eq("id", editing.id);
        if (error) throw error;
        toast.success("Template aktualisiert");
      } else {
        const { error } = await supabase
          .from("landing_page_templates")
          .insert({
            name: form.name,
            description: form.description || null,
            category: form.category || null,
            is_active: form.is_active,
            preview_image_url: form.preview_image_url || null,
            template_data: parsed,
          });
        if (error) throw error;
        toast.success("Template erstellt");
      }
      setModalOpen(false);
      await load();
    } catch (e) {
      console.error(e);
      toast.error("Speichern fehlgeschlagen");
    }
  };

  const remove = async (id: string) => {
    try {
      const { error } = await supabase.from("landing_page_templates").delete().eq("id", id);
      if (error) throw error;
      toast.success("Template gelöscht");
      await load();
    } catch (e) {
      console.error(e);
      toast.error("Löschen fehlgeschlagen");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Landing Page Templates</CardTitle>
        <CardDescription>Verwalten Sie die Vorlagen für den Builder.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex justify-end">
          <Dialog open={modalOpen} onOpenChange={setModalOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate}>
                <Plus className="h-4 w-4 mr-2" /> Neues Template
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader>
                <DialogTitle>{editing ? "Template bearbeiten" : "Neues Template"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Name</Label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  </div>
                  <div>
                    <Label>Kategorie</Label>
                    <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label>Beschreibung</Label>
                  <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
                <div>
                  <Label>Vorschau-Bild URL</Label>
                  <Input value={form.preview_image_url} onChange={(e) => setForm({ ...form, preview_image_url: e.target.value })} />
                </div>
                <div>
                  <Label>Template JSON</Label>
                  <textarea
                    className="w-full h-56 rounded border p-2 font-mono text-sm"
                    value={form.template_data}
                    onChange={(e) => setForm({ ...form, template_data: e.target.value })}
                  />
                </div>
                <div className="flex justify-end">
                  <Button onClick={save} disabled={!form.name}>Speichern</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Kategorie</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map(t => (
                <TableRow key={t.id}>
                  <TableCell>
                    <div className="font-medium">{t.name}</div>
                    <div className="text-xs text-muted-foreground">{t.description}</div>
                  </TableCell>
                  <TableCell>{t.category || "-"}</TableCell>
                  <TableCell>{t.is_active ? <Badge>Aktiv</Badge> : <Badge variant="secondary">Inaktiv</Badge>}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => openEdit(t)}>
                        <Edit className="h-4 w-4 mr-1" /> Bearbeiten
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => remove(t.id)} className="text-red-600">
                        <Trash2 className="h-4 w-4 mr-1" /> Löschen
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
